import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useSearchContent } from '../../../hooks/useSearchContent';
import type { SearchContentItem } from '../../../hooks/useSearchContent';
import { addChannelItem } from '../../../api/marketplaceApi';
import { ChannelItemType } from 'types/channel';
import CheckboxIcon from '../../atoms/CheckboxIcon/CheckboxIcon';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import type { DropdownChangeEvent } from '../../atoms/Dropdown/DropdownAtom';
import SearchBarAtom from '../../atoms/SearchBar/SearchBarAtom';
import './AddItemDialogMolecule.scss';

export const TYPE_OPTIONS: Array<{ value: ChannelItemType | 'all'; key: string }> = [
  { value: 'all', key: 'marketplace.channelManage.content.filterOptions.all' },
  { value: 'approach', key: 'marketplace.channelManage.content.filterOptions.approach' },
  { value: 'surface_template', key: 'marketplace.channelManage.content.filterOptions.surfaceTemplate' },
  { value: 'schema', key: 'marketplace.channelManage.content.filterOptions.schema' },
  { value: 'context', key: 'marketplace.channelManage.content.filterOptions.context' },
  { value: 'arc_template', key: 'marketplace.channelManage.content.filterOptions.arcTemplate' },
  { value: 'orbit_template', key: 'marketplace.channelManage.content.filterOptions.orbitTemplate' },
  { value: 'metaspace', key: 'marketplace.channelManage.content.filterOptions.metaspace' },
];

const RESULT_TYPE_KEYS: Partial<Record<ChannelItemType, string>> = {
  approach: 'marketplace.channelManage.content.filterOptions.approach',
  surface_template: 'marketplace.channelManage.content.filterOptions.surfaceTemplate',
  schema: 'marketplace.channelManage.content.filterOptions.schema',
  context: 'marketplace.channelManage.content.filterOptions.context',
  arc_template: 'marketplace.channelManage.content.filterOptions.arcTemplate',
  orbit_template: 'marketplace.channelManage.content.filterOptions.orbitTemplate',
  metaspace: 'marketplace.channelManage.content.filterOptions.metaspace',
};

export interface AddItemDialogMoleculeProps {
  open: boolean;
  channelId: string;
  onClose: () => void;
  onAdded: () => void;
}

const AddItemDialogMolecule: React.FC<AddItemDialogMoleculeProps> = ({
  open,
  channelId,
  onClose,
  onAdded,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const { results, loading, error: searchError, search, setContentType, contentType } =
    useSearchContent();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setSelected(new Set());
    setSearchInput('');
    setSubmitting(false);
    setError(null);
    setContentType('all');
    search('');
  }, [setContentType, search]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      search(value);
    },
    [search]
  );

  const handleTypeChange = useCallback(
    (e: DropdownChangeEvent) => {
      setContentType(e.value as ChannelItemType | 'all');
      setSelected(new Set());
    },
    [setContentType]
  );

  const dropdownOptions = useMemo(
    () => TYPE_OPTIONS.map((opt) => ({ label: t(opt.key), value: opt.value })),
    [t]
  );

  const handleToggleItem = useCallback((item: SearchContentItem) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      return next;
    });
  }, []);

  const selectedItems = useMemo(() => {
    return results.filter((item) => selected.has(item.id));
  }, [results, selected]);

  const searchPlaceholder = useMemo(() => {
    if (contentType === 'surface_template') {
      return t('marketplace.channelManage.addItemDialog.searchPlaceholderTemplate');
    }
    return t('marketplace.channelManage.addItemDialog.searchPlaceholder');
  }, [contentType, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (selectedItems.length === 0 || !apiClients) return;

      setSubmitting(true);
      setError(null);

      try {
        for (const item of selectedItems) {
          await addChannelItem(apiClients.schemaClient, channelId, {
            itemType: item.type,
            itemId: item.id,
            metadata: {
              name: { default: item.name },
              ...(item.description ? { description: { default: item.description } } : {}),
              ...(item.tags ? { tags: item.tags } : {}),
            },
          });
        }

        resetState();
        onAdded();
      } catch (err) {
        setError(t('marketplace.channelManage.addItemDialog.addError'));
      } finally {
        setSubmitting(false);
      }
    },
    [selectedItems, apiClients, channelId, resetState, onAdded, t]
  );

  if (!open) return null;

  return (
    <div
      className="marketplace-dialog-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className="marketplace-dialog marketplace-add-item-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-item-dialog-title"
      >
        <div className="marketplace-dialog__header">
          <h2 id="add-item-dialog-title" className="marketplace-dialog__title">
            {t('marketplace.channelManage.addItemDialog.title')}
          </h2>
          <button
            type="button"
            className="marketplace-dialog__close-btn"
            onClick={handleClose}
            aria-label={t('common.actions.close')}
          >
            &times;
          </button>
        </div>

        <form className="marketplace-dialog__body" onSubmit={handleSubmit}>
          {/* Type filter */}
          <div className="marketplace-dialog__field">
            <label className="marketplace-dialog__label" htmlFor="add-item-type">
              {t('marketplace.channelManage.addItemDialog.typeLabel')}
            </label>
            <DropdownAtom
              inputId="add-item-type"
              className="marketplace-add-item-dialog__type-dropdown"
              value={contentType}
              options={dropdownOptions}
              onChange={handleTypeChange}
              disabled={submitting}
            />
          </div>

          {/* Search input */}
          <SearchBarAtom
            className="marketplace-add-item-dialog__search"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            isSearching={loading}
            disabled={submitting}
          />

          {/* Results list */}
          <div className="marketplace-add-item-dialog__results">
            {loading && (
              <div className="marketplace-add-item-dialog__loading">
                <i className="pi pi-spin pi-spinner" />
              </div>
            )}

            {!loading && searchError && (
              <div className="marketplace-add-item-dialog__error-message">
                {t('marketplace.channelManage.addItemDialog.searchError')}
              </div>
            )}

            {!loading && !searchError && results.length === 0 && (
              <div className="marketplace-add-item-dialog__no-results">
                {t('marketplace.channelManage.addItemDialog.noResults')}
              </div>
            )}

            {!loading &&
              results.map((item) => (
                <label
                  key={item.id}
                  className={[
                    'marketplace-add-item-dialog__result-item',
                    selected.has(item.id) ? 'marketplace-add-item-dialog__result-item--selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <input
                    type="checkbox"
                    className="marketplace-add-item-dialog__checkbox--hidden"
                    checked={selected.has(item.id)}
                    onChange={() => handleToggleItem(item)}
                    disabled={submitting}
                    aria-label={item.name}
                  />
                  <CheckboxIcon isSelected={selected.has(item.id)} />
                  <div className="marketplace-add-item-dialog__result-info">
                    <span className="marketplace-add-item-dialog__result-name">
                      {item.name}
                    </span>
                    {item.description && (
                      <span className="marketplace-add-item-dialog__result-desc">
                        {item.description}
                      </span>
                    )}
                  </div>
                  <span className="marketplace-add-item-dialog__result-type">
                    {t(RESULT_TYPE_KEYS[item.type] ?? 'marketplace.channelManage.content.filterOptions.approach')}
                  </span>
                </label>
              ))}
          </div>

          {/* Selected count */}
          {selected.size > 0 && (
            <div className="marketplace-add-item-dialog__selected-count">
              {t(
                selected.size === 1
                  ? 'marketplace.channelManage.addItemDialog.selectedCount'
                  : 'marketplace.channelManage.addItemDialog.selectedCount_plural',
                { count: selected.size }
              )}
            </div>
          )}

          {/* Error */}
          {error && <div className="marketplace-dialog__error">{error}</div>}

          {/* Actions */}
          <div className="marketplace-dialog__actions">
            <button
              type="button"
              className="marketplace-dialog__cancel-btn"
              onClick={handleClose}
              disabled={submitting}
            >
              {t('marketplace.channelManage.addItemDialog.cancelButton')}
            </button>
            <button
              type="submit"
              className="marketplace-dialog__submit-btn"
              disabled={selected.size === 0 || submitting}
            >
              {submitting ? (
                <>
                  <span className="marketplace-subscribe-btn__spinner" />
                  {t('marketplace.channelManage.addItemDialog.adding')}
                </>
              ) : (
                t('marketplace.channelManage.addItemDialog.addButton')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddItemDialogMolecule;
